import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { MicIcon } from '../components/icons'
import { api, apiJson } from '../lib/api'
import {
  canRecord,
  extensionFor,
  micErrorKey,
  startRecording,
  unlockAudio,
  type AudioCapabilities,
  type Recording,
  uploadVoiceMessage,
} from '../lib/speech'

/**
 * Mikrofon butonu - ses girisi.
 *
 * ## Buton KOSULSUZ gosterilmiyor
 *
 * Uc kosul birden saglanmali: sunucu STT'yi aciyor, `faster-whisper` kurulu,
 * ve tarayici `MediaRecorder` destekliyor. Biri eksikse buton HIC
 * gorunmuyor. Tiklandiginda hata veren bir buton gostermek, kullaniciya
 * ozelligin BOZUK oldugunu dusundururdu - oysa yalnizca kapali.
 *
 * ## Ses yalnizca KENDI sunucumuza gidiyor
 *
 * `webkitSpeechRecognition` HIC kullanilmiyor: Chrome onu Google
 * sunucularina gonderiyor ve projenin tum gerekcesi verinin makineden
 * cikmamasi. Ustune sayfanin CSP'si `connect-src 'self'`, yani tarayici
 * seviyesinde de baska bir hedefe gidemez.
 *
 * ## Transkript metnin UZERINE yazmiyor
 *
 * Var olan taslaga EKLENIYOR. Uzerine yazmak, kullanicinin yazdigi bir
 * cumleyi sessizce silmek olurdu ve geri alma yolu yok.
 */
export function MicButton({
  onText,
  onVoiceMessage,
  disabled,
}: {
  /** Dikte: metin yazi kutusuna eklenir (kutu doluysa). */
  onText(text: string): void
  /**
   * SESLI MESAJ: verilirse kayit bir EK olarak yuklenir (sunucu metne
   * ceviriyor) ve mesaj dogrudan gonderilir; mesajda oynatici + transkript
   * gorunur, yanit otomatik okunur. `null` = bu an dikte modunda (kutuda
   * metin var) - dikte sesi SAKLANMIYOR.
   */
  onVoiceMessage?: ((transcript: string, attachmentId: string) => void) | null
  disabled?: boolean
}) {
  const { t } = useTranslation()
  const [caps, setCaps] = useState<AudioCapabilities | null>(null)
  const [state, setState] = useState<'idle' | 'recording' | 'sending'>('idle')
  const [error, setError] = useState<string | null>(null)
  const recording = useRef<Recording | null>(null)

  useEffect(() => {
    let alive = true
    apiJson<AudioCapabilities>('/api/audio/capabilities')
      .then((value) => {
        if (alive) setCaps(value)
      })
      // Yetenek sorgusu basarisiz olursa buton gorunmuyor. Sessizce
      // gostermek, tiklandiginda hata veren bir butona doner.
      .catch(() => undefined)
    return () => {
      alive = false
    }
  }, [])

  // Bilesen kaldirilirken mikrofon SERBEST birakilmali. Atlanirsa tarayici
  // sekmesindeki kirmizi kayit noktasi sekme kapanana kadar kaliyor -
  // kullaniciya "hala dinleniyor" gostermek.
  useEffect(() => {
    return () => recording.current?.cancel()
  }, [])

  // `caps?.stt?.enabled` - IKI opsiyonel zincir.
  //
  // `caps?.stt.enabled` yazmak bir hata ve testler onu yakaladi: zincir
  // yalnizca `caps`i koruyor, `stt` alani eksik bir govdede `.enabled`
  // PATLIYOR. Gercek senaryosu da var - eski bir sunucu surumu bu alani
  // gondermezse composer TAMAMEN cokuyor.
  // Guvensiz baglamda (duz http, ornegin hedefai.local:8080) tarayici
  // `navigator.mediaDevices`i HIC vermiyor. Dugmeyi gizlemek kullaniciyi
  // "mikrofon ozelligi neden yok" sorusuyla birakirdi; gosterip tiklaninca
  // https gerektigini soyluyoruz.
  const secure = typeof window === 'undefined' || window.isSecureContext !== false
  if (!caps?.stt?.enabled || (secure && !canRecord())) return null

  async function toggle() {
    setError(null)

    if (state === 'recording') {
      const active = recording.current
      recording.current = null
      setState('sending')
      try {
        const blob = await active!.stop()
        if (onVoiceMessage) {
          const voice = await uploadVoiceMessage(blob)
          // Sessiz/anlasilmayan kayit GONDERILMIYOR: model bos bir mesaja
          // "ne demek istediniz" diye yanit verirdi. Yuklenen ek hicbir
          // mesaja baglanmadigi icin temizlikte siliniyor.
          if (voice.transcript) onVoiceMessage(voice.transcript, voice.id)
          else setError(t('voice.mic.empty'))
        } else {
          const text = await upload(blob)
          if (text) onText(text)
        }
      } catch {
        setError(t('voice.mic.failed'))
      } finally {
        setState('idle')
      }
      return
    }

    if (!secure) {
      setError(t('voice.mic.insecure'))
      return
    }
    // Tiklama ANINDA: yanit onlarca saniye sonra geldiginde tarayici
    // otomatik oynatmayi reddetmesin (bkz. `unlockAudio`).
    if (onVoiceMessage) unlockAudio()
    try {
      recording.current = await startRecording()
      setState('recording')
    } catch (cause) {
      // Sebep tarayicinin hata adindan (bkz. `micErrorKey`): "izin
      // verilmedi" her zaman dogru degil - sayfa basligi, cihaz yoklugu veya
      // mikrofonu kullanan baska bir uygulama da ayni yere dusuyor.
      setError(t(micErrorKey(cause)))
    }
  }

  const label =
    state === 'recording'
      ? t('voice.mic.stop')
      : state === 'sending'
        ? t('voice.mic.sending')
        : t('voice.mic.start')

  return (
    <>
      <button
        type="button"
        className={state === 'recording' ? 'icon-btn recording' : 'icon-btn'}
        onClick={() => void toggle()}
        disabled={disabled || state === 'sending'}
        // Kayit durumu ekran okuyucuya SOYLENMELI: kirmizi bir nokta
        // gormeyen kullanici kaydin basladigini bilemez.
        aria-pressed={state === 'recording'}
        aria-label={label}
        title={label}
      >
        {state === 'sending' ? '…' : <MicIcon />}
      </button>
      {error && (
        <span className="composer-error" role="alert">
          {error}
        </span>
      )}
    </>
  )
}

async function upload(blob: Blob): Promise<string> {
  const body = new FormData()
  // Uzanti SUNUCUNUN allowlist'i icin: dosya adi olmadan `.webm` cikarimi
  // yapilamiyor ve istek 415 aliyor.
  body.append('file', blob, `kayit.${extensionFor(blob.type)}`)
  body.append('language', 'tr')

  // `api()` kullaniliyor (ham `fetch` degil): CSRF header'ini otomatik
  // ekliyor. `Content-Type` ELLE VERILMIYOR - `FormData` icin tarayicinin
  // multipart boundary'sini kendisi yazmasi gerekiyor.
  const response = await api('/api/transcribe', { method: 'POST', body })
  if (!response.ok) throw new Error(String(response.status))
  const data = (await response.json()) as { text?: string }
  return (data.text ?? '').trim()
}
